import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import EmailTemplatePicker from "./EmailTemplatePicker";
import { useEmailSettings } from "./useEmailSettings";

const field = "bg-slate-900 border-slate-700 text-slate-100 text-sm";
const btn = "px-3 py-2 rounded-md text-xs border disabled:opacity-40";

export function EmailPreview({ preview, t }) {
  return (
    <div className="rounded-md border border-slate-700 overflow-hidden" data-testid="email-preview">
      <div className="px-3 py-2 text-xs bg-slate-900 text-slate-300 space-y-0.5">
        <div>{t("admin.emailTo")}: <span className="font-mono-data">{preview.to}</span></div>
        <div>{t("admin.emailSubject")}: <span className="text-slate-100" data-testid="email-preview-subject">{preview.subject}</span></div>
      </div>
      <iframe title="email-preview" srcDoc={preview.html} sandbox="" className="w-full h-80 bg-white" data-testid="email-preview-frame" />
    </div>
  );
}

export default function AdminEmailDialog({ open, onOpenChange, target, onSent, t }) {
  const { test_email: testEmail } = useEmailSettings();
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  useEffect(() => { if (open) { setSubject(""); setBody(""); setPreview(null); setError(""); } }, [open]);

  const ready = subject.trim() && body.trim() && !busy;
  const edit = (setter) => (e) => { setter(e.target.value); setPreview(null); };
  const errMsg = (e) => e.response?.data?.detail || t("admin.emailNetworkError");

  const loadPreview = async () => {
    setBusy("preview"); setError("");
    try {
      const { data } = await api.post(`/admin/users/${target.id}/email/preview`, { subject, body });
      setPreview(data);
    } catch (e) { setError(errMsg(e)); }
    setBusy("");
  };
  const send = async (test) => {
    setBusy(test ? "test" : "send"); setError("");
    try {
      const { data } = await api.post(`/admin/users/${target.id}/email`, { subject, body, test });
      toast.success(t(test ? "admin.emailTestSent" : "admin.emailSent", { to: data.recipient }));
      onSent();
      if (!test) onOpenChange(false);
    } catch (e) { setError(errMsg(e)); onSent(); }
    setBusy("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-slate-950 border-slate-800 text-slate-100" data-testid="admin-email-dialog">
        <DialogHeader>
          <DialogTitle>{t("admin.sendEmail")}</DialogTitle>
          <DialogDescription className="text-slate-400">{t("admin.emailLimit")}</DialogDescription>
        </DialogHeader>
        <EmailTemplatePicker subject={subject} body={body} t={t}
          onPick={(tpl) => { setSubject(tpl.subject); setBody(tpl.body); setPreview(null); }} />
        <label className="block text-xs text-slate-400">{t("admin.emailTo")}
          <Input value={target.email} readOnly disabled className={`${field} mt-1 font-mono-data`} data-testid="email-recipient" />
        </label>
        <label className="block text-xs text-slate-400">{t("admin.emailSubject")}
          <Input value={subject} onChange={edit(setSubject)} maxLength={200} className={`${field} mt-1`} data-testid="email-subject" />
        </label>
        <label className="block text-xs text-slate-400">{t("admin.emailBody")}
          <Textarea value={body} onChange={edit(setBody)} rows={10} maxLength={10000} className={`${field} mt-1 font-sans`} data-testid="email-body" />
        </label>
        <div className="text-[11px] text-slate-500" data-testid="email-placeholders">{t("admin.emailPlaceholders")}</div>
        {preview && <EmailPreview preview={preview} t={t} />}
        {error && <div role="alert" className="text-sm text-red-300 border border-red-500/40 bg-red-500/10 rounded-md px-3 py-2" data-testid="email-error">{error}</div>}
        <DialogFooter className="gap-2 sm:gap-2 flex-wrap sticky -bottom-6 -mx-6 px-6 py-3 bg-slate-950 border-t border-slate-800 z-10">
          <button onClick={loadPreview} disabled={!ready} className={`${btn} border-slate-600 text-slate-200`} data-testid="email-preview-btn">{busy === "preview" ? t("common.loading") : t("admin.emailPreview")}</button>
          <button onClick={() => send(true)} disabled={!ready} className={`${btn} border-slate-600 text-slate-200`} data-testid="email-send-test-btn">{busy === "test" ? t("common.loading") : `${t("admin.emailSendTest")} (${testEmail || "…"})`}</button>
          <button onClick={() => send(false)} disabled={!ready || !preview} title={!preview ? t("admin.emailPreviewFirst") : ""} className={`${btn} border-amber-500 bg-amber-500 text-slate-950 font-semibold`} data-testid="email-send-btn">{busy === "send" ? t("common.loading") : t("admin.emailSend")}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
